import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ShieldCheck, FileCheck, RotateCcw } from 'lucide-react';

export type LegalTabType = 'privacy' | 'acceptable-use' | 'refund-policy';

interface PrivacyProps {
  defaultTab?: LegalTabType;
}

const getTabFromHash = (hash: string, fallback: LegalTabType = 'privacy'): LegalTabType => {
  const cleanHash = hash.replace('#', '').toLowerCase();
  if (cleanHash === 'acceptable-use' || cleanHash === 'acceptable-use-policy') {
    return 'acceptable-use';
  }
  if (cleanHash === 'refund' || cleanHash === 'refund-policy') {
    return 'refund-policy';
  }
  if (cleanHash === 'privacy' || cleanHash === 'privacy-policy') {
    return 'privacy';
  }
  return fallback;
};

const Privacy: React.FC<PrivacyProps> = ({ defaultTab = 'privacy' }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const contentRef = useRef<HTMLDivElement>(null);

  const [activeTab, setActiveTab] = useState<LegalTabType>(() =>
    getTabFromHash(location.hash, defaultTab)
  );

  useEffect(() => {
    setActiveTab(getTabFromHash(location.hash, defaultTab));
  }, [location.hash, defaultTab]);

  const handleTabChange = (tab: LegalTabType) => {
    setActiveTab(tab);
    navigate(`#${tab}`, { replace: true });
    if (contentRef.current) {
      const rect = contentRef.current.getBoundingClientRect();
      if (rect.top < 100) {
        contentRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const tabs: { id: LegalTabType; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
    { id: 'privacy', label: 'Privacy Policy', icon: ShieldCheck },
    { id: 'acceptable-use', label: 'Acceptable Use Policy', icon: FileCheck },
    { id: 'refund-policy', label: 'Refund Policy', icon: RotateCcw },
  ];

  return (
    <div className="w-full overflow-hidden pt-24">
      {/* Privacy Hero Section */}
      <section className="relative w-full bg-gradient-to-b from-[#e8f5f0] via-[#f0faf7] to-[#f8fcfb] py-16 md:py-24 overflow-hidden">
        <div className="w-full px-0">
          <div className="text-center mb-8 px-4 sm:px-6 lg:px-8">
            <h1 className="text-5xl sm:text-6xl md:text-7xl font-extrabold tracking-tight mb-6">
              <span className="text-emerald-700">Privacy</span> <span className="text-orange-500">&</span> <span className="text-emerald-700">Legal</span>
            </h1>
            <p className="text-lg sm:text-xl md:text-2xl text-slate-700 leading-relaxed max-w-3xl mx-auto">
              {activeTab === 'privacy' && 'Learn how we protect your data and handle your information'}
              {activeTab === 'acceptable-use' && 'Guidelines for safe, honest, and respectful use across MarketMate'}
              {activeTab === 'refund-policy' && 'Clear guidelines on order cancellations, eligibility, and refunds'}
            </p>
          </div>

          <div className="w-full">
            <img
              src="/images/Privacy and legal.png"
              alt="Privacy and Legal Protection"
              className="w-full h-auto object-cover"
            />
          </div>
        </div>
      </section>

      {/* Policies Content Section */}
      <section ref={contentRef} id="policy-content" className="relative w-full bg-[#e8f5f0] py-16 md:py-28 overflow-hidden">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Tab Pill Navigation */}
          <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3 mb-12">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  type="button"
                  className={`flex items-center gap-2 px-5 py-3 rounded-full text-sm sm:text-base font-semibold transition-all duration-200 cursor-pointer shadow-sm ${
                    isActive
                      ? 'bg-emerald-700 text-white shadow-emerald-700/20 shadow-md ring-2 ring-emerald-700 ring-offset-2'
                      : 'bg-white/90 text-slate-700 hover:bg-white hover:text-emerald-800 border border-slate-200'
                  }`}
                >
                  <Icon className={`w-4 h-4 sm:w-5 sm:h-5 ${isActive ? 'text-white' : 'text-emerald-600'}`} />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>

          {/* TAB 1: PRIVACY POLICY */}
          {activeTab === 'privacy' && (
            <div className="prose prose-lg max-w-none">
              <h2 className="text-4xl sm:text-5xl font-extrabold text-orange-500 mb-4">Privacy Policy</h2>
              <p className="text-slate-600 mb-8 font-semibold">Last updated: April 2026</p>

              <p className="text-slate-700 mb-6">
                At MarketMate, we're building a platform that connects farmers, riders, and customers to make fresh food easier to access. As part of that, we also take your privacy seriously.
              </p>
              <p className="text-slate-700 mb-8">
                This policy explains what information we collect, why we collect it, and how we use it.
              </p>

              <h3 className="text-2xl font-bold text-emerald-700 mt-8 mb-4">1. What We Collect</h3>
              <p className="text-slate-700 mb-4">
                When you use MarketMate, we collect only what we need to make the platform work smoothly.
              </p>

              <h4 className="text-xl font-semibold text-slate-800 mt-6 mb-3">Information you provide</h4>
              <ul className="list-disc list-inside text-slate-700 mb-6 space-y-2">
                <li>Your name and contact details (email, phone number)</li>
                <li>Delivery addresses</li>
                <li>Account and profile information</li>
                <li>Payment details (handled securely by trusted payment providers)</li>
              </ul>

              <h4 className="text-xl font-semibold text-slate-800 mt-6 mb-3">Information collected automatically</h4>
              <ul className="list-disc list-inside text-slate-700 mb-8 space-y-2">
                <li>Device and browser information</li>
                <li>App activity (what you browse, click, or interact with)</li>
                <li>Location data (only when needed for deliveries)</li>
              </ul>

              <h3 className="text-2xl font-bold text-emerald-700 mt-8 mb-4">2. Why We Collect It</h3>
              <p className="text-slate-700 mb-4">We use your information to make MarketMate work the way it should:</p>
              <ul className="list-disc list-inside text-slate-700 mb-8 space-y-2">
                <li>To connect you with farmers and vendors</li>
                <li>To process and deliver your orders</li>
                <li>To keep you updated on your orders and activity</li>
                <li>To improve how the platform works for you</li>
                <li>To keep the platform safe and prevent misuse</li>
              </ul>

              <h3 className="text-2xl font-bold text-emerald-700 mt-8 mb-4">3. Who We Share It With</h3>
              <p className="text-slate-700 mb-4">MarketMate works by connecting people, so some information is shared where necessary:</p>
              <ul className="list-disc list-inside text-slate-700 mb-6 space-y-2">
                <li>Farmers &amp; Vendors → to prepare your orders</li>
                <li>Riders → to deliver your orders</li>
                <li>Payment providers → to complete transactions securely</li>
                <li>Service partners → for analytics, support, and system improvements</li>
              </ul>
              <p className="text-slate-700 font-semibold mb-8">We never sell your personal data.</p>

              <h3 className="text-2xl font-bold text-emerald-700 mt-8 mb-4">4. How We Protect Your Data</h3>
              <p className="text-slate-700 mb-8">
                We use industry-standard security measures to protect your information. That said, no system is completely risk-free, but we continuously work to keep your data safe.
              </p>

              <h3 className="text-2xl font-bold text-emerald-700 mt-8 mb-4">5. Your Control</h3>
              <p className="text-slate-700 mb-4">You're always in control of your data. You can:</p>
              <ul className="list-disc list-inside text-slate-700 mb-8 space-y-2">
                <li>Update or correct your information</li>
                <li>Opt out of promotional messages</li>
                <li>Disable location access</li>
                <li>Request deletion of your account</li>
              </ul>

              <h3 className="text-2xl font-bold text-emerald-700 mt-8 mb-4">6. Cookies &amp; Tracking</h3>
              <p className="text-slate-700 mb-4">We use cookies and similar tools to:</p>
              <ul className="list-disc list-inside text-slate-700 mb-6 space-y-2">
                <li>Improve your experience</li>
                <li>Understand how people use MarketMate</li>
                <li>Make the platform faster and more relevant</li>
              </ul>
              <p className="text-slate-700 mb-8">You can manage cookies in your browser settings.</p>

              <h3 className="text-2xl font-bold text-emerald-700 mt-8 mb-4">7. How Long We Keep Data</h3>
              <p className="text-slate-700 mb-4">We keep your information only as long as necessary to:</p>
              <ul className="list-disc list-inside text-slate-700 mb-8 space-y-2">
                <li>Provide our services</li>
                <li>Meet legal and regulatory requirements</li>
              </ul>

              <h3 className="text-2xl font-bold text-emerald-700 mt-8 mb-4">8. Third-Party Services</h3>
              <p className="text-slate-700 mb-8">
                Some parts of MarketMate rely on third-party services (like payments). These services have their own privacy policies, which we encourage you to review.
              </p>

              <h3 className="text-2xl font-bold text-emerald-700 mt-8 mb-4">9. Updates to This Policy</h3>
              <p className="text-slate-700 mb-8">
                We may update this policy as MarketMate grows. When we do, we'll update the date above and notify you where necessary.
              </p>

              <h3 className="text-2xl font-bold text-emerald-700 mt-8 mb-4">10. Contact Us</h3>
              <p className="text-slate-700 mb-2">If you have questions about your data or this policy, reach out to us:</p>
              <p className="text-slate-700 text-lg">
                📧 <a href="mailto:support@marketmateconnectltd.com" className="text-emerald-600 hover:text-emerald-700 font-semibold">support@marketmateconnectltd.com</a><br />
                📞 <a href="https://wa.me/2348112560962" target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:text-emerald-700 font-semibold">+234 811 256 0962</a>
              </p>
            </div>
          )}

          {/* TAB 2: ACCEPTABLE USE POLICY */}
          {activeTab === 'acceptable-use' && (
            <div className="prose prose-lg max-w-none">
              <h2 className="text-4xl sm:text-5xl font-extrabold text-orange-500 mb-4">Acceptable Use Policy</h2>
              <p className="text-slate-600 mb-8 font-semibold">Last updated: April 2026</p>

              <p className="text-slate-700 mb-6">
                This Acceptable Use Policy (&quot;Policy&quot;) governs the use of the MarketMate platform, mobile application, and website (together, the &quot;Platform&quot;) by all users, including customers, vendors, riders, and any other person who accesses or uses the Platform (collectively, &quot;Users&quot;).
              </p>
              <p className="text-slate-700 mb-8">
                By using the Platform, you agree to comply with this Policy. This Policy should be read alongside our Terms of Service, Privacy Policy, and Refund Policy.
              </p>

              <h3 className="text-2xl font-bold text-emerald-700 mt-8 mb-4">1. Who This Policy Applies To</h3>
              <p className="text-slate-700 mb-4">This Policy applies to:</p>
              <ul className="list-disc list-inside text-slate-700 mb-8 space-y-2">
                <li><strong className="text-slate-800">Customers</strong> who browse and purchase products on MarketMate</li>
                <li><strong className="text-slate-800">Vendors</strong> (including farmers, wholesalers, and foodstuff sellers) who list and sell products through MarketMate</li>
                <li><strong className="text-slate-800">Riders</strong> who provide delivery services through MarketMate</li>
                <li>Any other person or entity that accesses or interacts with the Platform</li>
              </ul>

              <h3 className="text-2xl font-bold text-emerald-700 mt-8 mb-4">2. Acceptable Use</h3>
              <p className="text-slate-700 mb-4">
                Users agree to use the Platform only for its intended purpose: browsing, buying, selling, and delivering foodstuff and related products in a lawful, honest, and respectful manner. Users must:
              </p>
              <ul className="list-disc list-inside text-slate-700 mb-8 space-y-2">
                <li>Provide accurate, current, and complete information during registration and at all times while using the Platform</li>
                <li>Use the Platform in compliance with all applicable Nigerian laws and regulations</li>
                <li>Treat other Users, MarketMate staff, and support agents with respect and courtesy</li>
                <li>Only list, sell, or request products that are lawful and, where applicable, meet relevant food safety and handling standards</li>
                <li>Keep account credentials (including PINs used for delivery/parcel verification) confidential and secure</li>
                <li>Promptly report any suspected fraud, abuse, or security issue through MarketMate's support channels</li>
              </ul>

              <h3 className="text-2xl font-bold text-emerald-700 mt-8 mb-4">3. Prohibited Conduct</h3>
              <p className="text-slate-700 mb-4">Users must <strong className="text-slate-900">not</strong>:</p>
              <ul className="list-disc list-inside text-slate-700 mb-8 space-y-2">
                <li>Provide false, misleading, or fraudulent information (including false identity, business, or bank details) during onboarding or at any other time</li>
                <li>List, sell, or attempt to sell counterfeit, stolen, expired, unsafe, spoiled, or illegal products</li>
                <li>List or sell any product outside MarketMate's permitted foodstuff and related-goods categories without prior written approval</li>
                <li>Use the Platform to launder money, finance illegal activity, or process transactions unrelated to genuine sales of goods or delivery services</li>
                <li>Circumvent MarketMate's payment system by soliciting or accepting off-platform payment for on-platform orders</li>
                <li>Harass, threaten, defraud, or discriminate against any other User, rider, vendor, or MarketMate staff member</li>
                <li>Misuse the split-buy, gift order, referral, promotional, or cashback/wallet features to obtain rewards or discounts through fraudulent, duplicate, or fictitious accounts or orders</li>
                <li>Falsify delivery, pickup, or pay-on-delivery confirmations (including PIN verification or payment proof) or otherwise interfere with the integrity of the order and delivery process</li>
                <li>Attempt to access, tamper with, reverse-engineer, or disrupt the Platform, its systems, or other Users' accounts</li>
                <li>Use bots, scrapers, or automated tools to access the Platform without MarketMate's written permission</li>
                <li>Impersonate another person, vendor, or entity, or misrepresent your affiliation with any person or entity</li>
              </ul>

              <h3 className="text-2xl font-bold text-emerald-700 mt-8 mb-4">4. Vendor-Specific Obligations</h3>
              <p className="text-slate-700 mb-4">In addition to the general rules above, vendors must:</p>
              <ul className="list-disc list-inside text-slate-700 mb-8 space-y-2">
                <li>Complete MarketMate's vendor verification process, including providing valid identification, business information (where applicable), and bank details, before being approved to sell</li>
                <li>Maintain accurate product listings, pricing, and stock levels</li>
                <li>Fulfill accepted orders promptly and in accordance with the quality and description presented on the Platform</li>
                <li>Cooperate with MarketMate's dispute-resolution and support process when a customer raises an issue</li>
                <li>Comply with any applicable licensing, food-handling, or business registration requirements under Nigerian law</li>
              </ul>

              <h3 className="text-2xl font-bold text-emerald-700 mt-8 mb-4">5. Rider-Specific Obligations</h3>
              <p className="text-slate-700 mb-4">Riders must:</p>
              <ul className="list-disc list-inside text-slate-700 mb-8 space-y-2">
                <li>Complete any required verification before accepting delivery or parcel jobs</li>
                <li>Handle customer orders and third-party parcels with care, and complete pickup/handover verification (including PIN checks and, for pay-on-delivery orders, accurate payment-proof submission) honestly</li>
                <li>Not open, tamper with, or misappropriate any order or parcel in their custody</li>
                <li>Represent MarketMate professionally while performing deliveries</li>
              </ul>

              <h3 className="text-2xl font-bold text-emerald-700 mt-8 mb-4">6. Enforcement</h3>
              <p className="text-slate-700 mb-4">
                MarketMate reserves the right, at its sole discretion and without prior notice, to:
              </p>
              <ul className="list-disc list-inside text-slate-700 mb-6 space-y-2">
                <li>Investigate suspected violations of this Policy</li>
                <li>Suspend or restrict a User's access to the Platform, including a vendor's ability to sell or a rider's ability to accept jobs</li>
                <li>Cancel or reverse orders, transactions, or promotional rewards obtained in violation of this Policy</li>
                <li>Withhold or reclaim payouts connected to fraudulent or policy-violating activity</li>
                <li>Report suspected illegal activity to the relevant authorities, including law enforcement and our payment service providers, where required</li>
              </ul>
              <p className="text-slate-700 font-semibold mb-8">
                Repeated or serious violations may result in permanent suspension or termination of a User's account.
              </p>

              <h3 className="text-2xl font-bold text-emerald-700 mt-8 mb-4">7. Reporting Violations</h3>
              <p className="text-slate-700 mb-8">
                If you become aware of any activity on the Platform that may violate this Policy, please report it through MarketMate's in-app support channel or contact us directly at{' '}
                <a href="mailto:support@marketmateconnectltd.com" className="text-emerald-600 hover:text-emerald-700 font-semibold">
                  support@marketmateconnectltd.com
                </a>.
              </p>

              <h3 className="text-2xl font-bold text-emerald-700 mt-8 mb-4">8. Changes to This Policy</h3>
              <p className="text-slate-700 mb-8">
                MarketMate may update this Policy from time to time. Continued use of the Platform after any update constitutes acceptance of the revised Policy. We encourage Users to review this page periodically.
              </p>

              <h3 className="text-2xl font-bold text-emerald-700 mt-8 mb-4">9. Contact Us</h3>
              <p className="text-slate-700 mb-2">For questions about this Policy, please reach out to:</p>
              <div className="bg-white/80 rounded-2xl p-6 border border-emerald-100 shadow-sm not-prose mb-8">
                <p className="font-bold text-slate-900 text-lg mb-1">MarketMate Connect Ltd</p>
                <p className="text-slate-700 mb-2">Operating across Nigeria</p>
                <p className="text-slate-700">
                  📧 <a href="mailto:support@marketmateconnectltd.com" className="text-emerald-600 hover:text-emerald-700 font-semibold">support@marketmateconnectltd.com</a>
                </p>
                <p className="text-slate-700 mt-1">
                  📞 <a href="https://wa.me/2348112560962" target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:text-emerald-700 font-semibold">+234 811 256 0962</a>
                </p>
              </div>
            </div>
          )}

          {/* TAB 3: REFUND POLICY */}
          {activeTab === 'refund-policy' && (
            <div className="prose prose-lg max-w-none">
              <h2 className="text-4xl sm:text-5xl font-extrabold text-orange-500 mb-4">Refund Policy</h2>
              <p className="text-slate-600 mb-8 font-semibold">Last updated: April 2026</p>

              <p className="text-slate-700 mb-6">
                This Refund Policy explains when and how customers may be eligible for a refund on orders placed through the MarketMate platform (&quot;Platform&quot;).
              </p>
              <p className="text-slate-700 mb-8">
                It should be read alongside our Terms of Service and Acceptable Use Policy.
              </p>

              <h3 className="text-2xl font-bold text-emerald-700 mt-8 mb-4">1. Overview</h3>
              <p className="text-slate-700 mb-8">
                MarketMate is a marketplace that connects customers with independent vendors (farmers, wholesalers, and foodstuff sellers) and coordinates delivery through our rider network. Payments are processed through our licensed payment partner, Paystack. MarketMate does not act as an escrow service; however, our order and delivery process is designed to track every order through to confirmed delivery, and this Policy governs how refunds are handled when something goes wrong.
              </p>

              <h3 className="text-2xl font-bold text-emerald-700 mt-8 mb-4">2. When You May Be Eligible for a Refund</h3>
              <p className="text-slate-700 mb-4">You may be eligible for a full or partial refund in the following circumstances:</p>
              <ul className="list-disc list-inside text-slate-700 mb-8 space-y-2">
                <li><strong className="text-slate-800">Order cancellation:</strong> You cancel your order before the vendor has accepted or begun preparing it, or before a rider has been dispatched, in line with our cancellation window.</li>
                <li><strong className="text-slate-800">Unavailable or out-of-stock product:</strong> A vendor is unable to fulfil part or all of your order after payment.</li>
                <li><strong className="text-slate-800">Failed delivery:</strong> Your order could not be delivered due to a fault on MarketMate's, the vendor's, or the rider's side (for example, the rider was unable to reach you after reasonable attempts, or the order was lost or damaged in transit).</li>
                <li><strong className="text-slate-800">Significant order issues:</strong> The item(s) delivered are materially different from what you ordered (wrong item, incorrect quantity, spoiled/expired product, or damaged goods).</li>
                <li><strong className="text-slate-800">Duplicate or failed payment charges:</strong> You were charged more than once for the same order, or a payment failed but funds were deducted.</li>
                <li><strong className="text-slate-800">Split-buy groups that do not fill:</strong> If a split-buy group you joined is not fully funded before its expiry window, your payment is automatically refunded.</li>
                <li><strong className="text-slate-800">Cancelled parcel deliveries:</strong> Parcel bookings cancelled before a rider is assigned are fully refunded; bookings cancelled after a rider is assigned but before pickup may incur a small cancellation fee, with the remainder refunded.</li>
              </ul>

              <h3 className="text-2xl font-bold text-emerald-700 mt-8 mb-4">3. When a Refund May Not Apply</h3>
              <p className="text-slate-700 mb-4">Refunds are generally <strong className="text-slate-900">not</strong> available where:</p>
              <ul className="list-disc list-inside text-slate-700 mb-6 space-y-2">
                <li>The order was delivered as described and accepted by you, and you simply changed your mind;</li>
                <li>You provided an incorrect delivery address or were unreachable at the time of delivery, and the order was returned or could not be delivered as a result;</li>
                <li>The issue arises from your own misuse, mishandling, or delay in reporting a problem after delivery;</li>
                <li>The request is made significantly after delivery, outside our reporting window (see Section 5).</li>
              </ul>
              <p className="text-slate-700 mb-8 italic">
                MarketMate reviews each case individually, and the above list is not exhaustive.
              </p>

              <h3 className="text-2xl font-bold text-emerald-700 mt-8 mb-4">4. Pay-on-Delivery Orders</h3>
              <p className="text-slate-700 mb-8">
                For orders paid on delivery, any applicable pay-on-delivery service fee and product cost are only collected once the order is verified as delivered. If a pay-on-delivery order is not fulfilled, no charge is applied and no refund is necessary; if a payment dispute arises (for example, over a cash or transfer payment), it will be investigated through our support process before any resolution is issued.
              </p>

              <h3 className="text-2xl font-bold text-emerald-700 mt-8 mb-4">5. How to Request a Refund</h3>
              <ol className="list-decimal list-inside text-slate-700 mb-8 space-y-3">
                <li>
                  <strong className="text-slate-800">Report the issue promptly:</strong> Contact us through MarketMate's in-app customer support / order-issue channel as soon as possible, and in any case within <strong className="text-slate-900">48 hours</strong> of delivery (or of the scheduled delivery time, if the order was not delivered).
                </li>
                <li>
                  <strong className="text-slate-800">Provide details:</strong> Share your order number and a clear description of the issue, along with supporting evidence (e.g. photos of damaged or incorrect produce) where applicable.
                </li>
                <li>
                  <strong className="text-slate-800">Review &amp; Investigation:</strong> Our support team will review your report, and where necessary, contact the relevant vendor and/or rider as part of the investigation.
                </li>
              </ol>

              <h3 className="text-2xl font-bold text-emerald-700 mt-8 mb-4">6. How Refunds Are Processed</h3>
              <ul className="list-disc list-inside text-slate-700 mb-8 space-y-2">
                <li>Where a refund is approved, it will be processed back through Paystack to the original payment method used, or credited to your MarketMate wallet, at MarketMate's discretion or your preference where offered.</li>
                <li>Refunds are typically processed within <strong className="text-slate-900">3–5 business days</strong> of approval, though your bank or card issuer may take additional time to reflect the funds.</li>
                <li>Where an order was partially fulfilled, a partial refund may be issued for the unfulfilled portion only.</li>
                <li>Delivery fees are refunded only where the delivery itself failed or was not MarketMate's, the vendor's, or the rider's fault to charge (for example, delivery fees are not refunded if you simply change your mind after a successful delivery).</li>
              </ul>

              <h3 className="text-2xl font-bold text-emerald-700 mt-8 mb-4">7. Disputed Refunds</h3>
              <p className="text-slate-700 mb-8">
                If you disagree with the outcome of a refund review, you may request that the issue be escalated for further review through our support channel. MarketMate's decision following escalation is final, without prejudice to any rights you may have under applicable Nigerian consumer protection law.
              </p>

              <h3 className="text-2xl font-bold text-emerald-700 mt-8 mb-4">8. Changes to This Policy</h3>
              <p className="text-slate-700 mb-8">
                MarketMate may update this Refund Policy from time to time. The version in effect at the time you place an order will govern that order. We encourage you to review this page periodically.
              </p>

              <h3 className="text-2xl font-bold text-emerald-700 mt-8 mb-4">9. Contact Us</h3>
              <p className="text-slate-700 mb-2">For refund requests or questions about this Policy, please contact us through the in-app support channel or at:</p>
              <div className="bg-white/80 rounded-2xl p-6 border border-emerald-100 shadow-sm not-prose mb-8">
                <p className="font-bold text-slate-900 text-lg mb-1">MarketMate Connect Ltd</p>
                <p className="text-slate-700 mb-2">Customer Support &amp; Disputes</p>
                <p className="text-slate-700">
                  📧 <a href="mailto:support@marketmateconnectltd.com" className="text-emerald-600 hover:text-emerald-700 font-semibold">support@marketmateconnectltd.com</a>
                </p>
                <p className="text-slate-700 mt-1">
                  📞 <a href="https://wa.me/2348112560962" target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:text-emerald-700 font-semibold">+234 811 256 0962</a>
                </p>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Privacy;
